import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Member } from '@sendbird/chat/groupChannel';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SendbirdChatService } from '../../sendbird-chat.service';

const MAX_LENGTH = 512; // 最大字數限制
const MESSAGE_COOLDOWN = 1000; // 訊息冷卻時間（毫秒）- 1秒內不能重複發送
@UntilDestroy()
@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [CommonModule, NgClass],
  templateUrl: './chat-input.component.html',
  styleUrls: ['./chat-input.component.scss'],
})
export class ChatInputComponent implements ControlValueAccessor {
  private userList: Member[] = [];
  private savedRange: Range | null = null;
  private _onChange: (value: unknown) => void = () => {};
  private _onTouched: () => void = () => {};
  private lastMessageTime = 0; // 上次發送訊息的時間戳

  protected _value: unknown = '';
  protected _disabled = false;
  showMaxLengthWarning: boolean = false;
  showMentionList = false;
  filteredUserList: Member[] = [];
  displayEmojiPicker = false;
  overlayRef!: OverlayRef;

  emojiList: string[] = [
    '😀',
    '😃',
    '😄',
    '😁',
    '😆',
    '😅',
    '🤣',
    '😂',
    '🙂',
    '🙃',
    '🫠',
    '😉',
    '😊',
    '😇',
    '🥰',
    '😍',
    '🤩',
    '😘',
    '😗',
    '😚',
    '😙',
    '🥲',
    '😋',
    '😛',
    '😜',
    '🤪',
    '😝',
    '🤑',
  ];

  @Output() sendMessage = new EventEmitter<void>();

  @Input()
  get value(): unknown {
    return this._value;
  }
  set value(newValue: unknown) {
    // 如果新值和当前值相同，则不做任何操作
    if (newValue === this._value) {
      return;
    }

    this._value = newValue;
    this._onChange(this._value);
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(newValue: unknown) {
    this._disabled = coerceBooleanProperty(newValue);
  }

  @Output() userSelected = new EventEmitter<Member>();
  @ViewChild('editor') editorRef!: ElementRef<HTMLDivElement>;
  @ViewChild('inputWrap') inputWrapRef!: ElementRef<HTMLDivElement>;
  @ViewChild('mentionTemplate') mentionTemplate!: TemplateRef<any>;
  @ViewChild('emojiTemplate') emojiTemplate!: TemplateRef<any>;

  constructor(
    private sendbirdChatService: SendbirdChatService,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private deviceService: DeviceDetectorService,
    @Optional() @Self() private ngControl: NgControl,
  ) {
    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: unknown): void {
    this._value = value || '';

    if (this._value === '') {
      this.showMaxLengthWarning = false; // 清除最大長度警告
    }

    if (this.editorRef) {
      this.editorRef.nativeElement.innerText = this._value as string;
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  ngOnInit(): void {
    this.sendbirdChatService.userList$.pipe(untilDestroyed(this)).subscribe(users => {
      this.userList = users;
    });
  }

  /**
   * 檢查是否可以發送訊息（防止1秒內重複發送）
   */
  private canSendMessage(): boolean {
    const now = Date.now();
    const timeSinceLastMessage = now - this.lastMessageTime;

    if (timeSinceLastMessage < MESSAGE_COOLDOWN) {
      return false;
    }

    // 更新最後發送時間
    this.lastMessageTime = now;
    return true;
  }

  onInput(): void {
    const el = this.editorRef.nativeElement;
    const text = el.innerText;

    if (text.length > MAX_LENGTH) {
      this.showMaxLengthWarning = true;
      // 如果输入超过最大长度，截断到最大长度
      el.innerText = text.slice(0, MAX_LENGTH);
      this.value = el.innerText;

      // 將 caret 移到最後
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    } else {
      this.showMaxLengthWarning = false;
      const isEmpty = el.innerText.trim() === '' || el.innerText === '\n';
      this.value = isEmpty ? '' : el.innerText.trim();
    }

    this.updateFilteredUserList();
  }

  onKeyDown(event: KeyboardEvent): void {
    const isMobile = this.deviceService.isMobile(navigator.userAgent);

    if (isMobile) {
      return;
    }

    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Shift + Enter: 允許換行（不阻止預設行為）
        return;
      } else {
        // 阻止 Enter 鍵的預設換行行為
        event.preventDefault();

        // 獲取當前輸入值並觸發提交事件
        const text = this.editorRef.nativeElement.innerText.trim();
        if (text) {
          // 檢查是否可以發送訊息（防止1秒內重複發送）
          if (!this.canSendMessage()) {
            return;
          }
          this.sendMessage.emit(); // 發送當前值
          this.closeFloatList(); // 關閉任何打開的浮動列表（如提及或表情）
        }
      }
    }
  }

  updateFilteredUserList(): void {
    const selection = window.getSelection();
    if (!selection || !selection.anchorNode) {
      return;
    }

    const node = selection.anchorNode;
    const offset = selection.anchorOffset;

    // 只使用當前 text node 的文字
    const textBeforeCaret = node.nodeType === Node.TEXT_NODE ? (node.textContent?.slice(0, offset) ?? '') : '';

    const match = textBeforeCaret.match(/(^|\s)@(\w*)$/);
    this.closeFloatList();

    if (match) {
      const keyword = match[2].toLowerCase();
      // show all matches so scrolling can reach every member
      this.filteredUserList = this.userList.filter(user => user.nickname.toLowerCase().includes(keyword));
      this.showMentionList = true;
      this.saveSelection();
      this.openFloatList(this.mentionTemplate);
    }
  }

  saveSelection() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    }
  }
  handleMentionSelect(user: Member): void {
    if (!this.savedRange) return;

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(this.savedRange);

    const range = selection?.getRangeAt(0);
    if (!range || range.startContainer.nodeType !== Node.TEXT_NODE) return;

    const textNode = range.startContainer as Text;
    const text = textNode.textContent ?? '';
    const offset = range.startOffset;

    const atIndex = text.lastIndexOf('@', offset);
    if (atIndex === -1) return;

    // 替換掉 "@xxx"
    const before = text.substring(0, atIndex);
    const after = text.substring(offset);
    const mentionTextNode = document.createTextNode(after);
    textNode.textContent = before;

    // 建立 mention span
    const mentionSpan = document.createElement('span');
    mentionSpan.className = 'mention';
    mentionSpan.textContent = `@${user.nickname}`;
    mentionSpan.dataset.userId = user.userId;
    mentionSpan.contentEditable = 'false';

    const space = document.createTextNode(' '); // &nbsp;

    // 插入順序：mention → 空白 → 原本文字尾巴
    range.setStart(textNode, before.length);
    range.setEnd(textNode, before.length);
    range.deleteContents();
    range.insertNode(mentionTextNode);
    range.insertNode(space);
    range.insertNode(mentionSpan);

    // 將 caret 放到空白後面
    const afterSpaceRange = document.createRange();
    afterSpaceRange.setStartAfter(space);
    afterSpaceRange.setEndAfter(space);
    selection?.removeAllRanges();
    selection?.addRange(afterSpaceRange);

    // 更新值並清理
    this.value = this.editorRef.nativeElement.innerText;
    this.savedRange = null;
    this.closeFloatList();
  }

  getMentionList(): string[] {
    const mentions: string[] = [];
    const editor = this.editorRef.nativeElement;
    const mentionElements = editor.querySelectorAll('span.mention');

    mentionElements.forEach((elem: Element) => {
      const htmlElem = elem as HTMLElement;
      const userId = htmlElem.dataset.userId;
      const user = this.userList.find(u => u.userId === userId);

      if (user) {
        mentions.push(user.userId);
      }
    });

    return mentions;
  }

  toggleEmojiPicker(): void {
    this.displayEmojiPicker = !this.displayEmojiPicker;
    this.closeFloatList();

    if (this.displayEmojiPicker) {
      // 如果顯示表情符號選擇器，則隱藏提及列表
      this.openFloatList(this.emojiTemplate);
    }
  }

  handleEmojiSelect(emoji: string): void {
    const editor = this.editorRef.nativeElement;
    const emojiNode = document.createTextNode(emoji + ' '); // 在表情後加一個空格

    editor.appendChild(emojiNode); // 將表情符號插入到編輯器中
    const range = document.createRange();
    const sel = window.getSelection();
    range.setStartAfter(emojiNode);
    range.setEndAfter(emojiNode);
    sel?.removeAllRanges();
    sel?.addRange(range); // 將 caret 移到表情符號後
    this.value = editor.innerText; // 更新 value
    this.closeFloatList();
  }

  openFloatList(template: TemplateRef<any>): void {
    const buttonRect = this.inputWrapRef.nativeElement.getBoundingClientRect();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.inputWrapRef.nativeElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ])
      .withDefaultOffsetY(-8);

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'transparent-backdrop',
      positionStrategy,
      width: buttonRect.width,
    });

    this.overlayRef.backdropClick().subscribe(() => this.closeFloatList());

    const portal = new TemplatePortal(template, this.vcr);
    this.overlayRef.attach(portal);
  }

  closeFloatList(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined!;
      this.displayEmojiPicker = false;
      this.showMentionList = false;
    }
  }
}
